/**
 * Injected by `authApi` login `transformResponse` when the token is read from
 * response headers (browser may omit unless `Access-Control-Expose-Headers` includes them).
 */
export const LOGIN_HEADER_TOKEN_FIELD = '__authTokenFromLoginResponseHeader';

const TOKEN_KEYS = [
  LOGIN_HEADER_TOKEN_FIELD,
  'token',
  'access_token',
  'accessToken',
  'jwt',
  'authentication_token',
  'auth_token',
  'authToken',
  'bearer',
  'id_token',
  'idToken',
  'api_token',
  'apiToken',
  'session_token',
  'sessionToken',
];

/**
 * Parse common Authorization / access-token style response headers.
 * @param {Response|undefined|null} response
 * @returns {string|null}
 */
export function readAuthTokenFromResponseHeaders(response) {
  if (!response || typeof response.headers?.get !== 'function') return null;
  const h = response.headers;
  const names = [
    'Authorization',
    'authorization',
    'access-token',
    'Access-Token',
    'x-access-token',
    'X-Access-Token',
    'x-auth-token',
    'X-Auth-Token',
  ];
  for (const name of names) {
    const raw = h.get(name);
    if (!raw || typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const bearer = trimmed.match(/^Bearer\s+(.+)$/i);
    if (bearer?.[1]) return bearer[1].trim();
    const devise = trimmed.match(/^Token\s+token=(?:"([^"]+)"|(\S+))/i);
    if (devise?.[1] || devise?.[2]) return (devise[1] || devise[2]).trim();
    if (/auth|access|token/i.test(name)) return trimmed;
  }
  return null;
}

function coerceTokenString(value) {
  if (typeof value === 'string') {
    const t = value.trim();
    return t.length > 0 ? t : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

/**
 * Depth-first search for known auth token keys (login JSON bodies vary a lot).
 * @param {unknown} obj
 * @param {number} depth
 * @param {number} maxDepth
 * @returns {string|null}
 */
function walkForToken(obj, depth, maxDepth) {
  if (depth > maxDepth || obj == null) return null;
  if (typeof obj !== 'object') return null;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const t = walkForToken(item, depth + 1, maxDepth);
      if (t) return t;
    }
    return null;
  }

  for (const key of TOKEN_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    const coerced = coerceTokenString(obj[key]);
    if (coerced) return coerced;
  }

  for (const v of Object.values(obj)) {
    if (v && typeof v === 'object') {
      const t = walkForToken(v, depth + 1, maxDepth);
      if (t) return t;
    }
  }
  return null;
}

/**
 * @param {unknown} result — value from `loginMutation().unwrap()` after `transformResponse`
 * @returns {string|null}
 */
export function extractPersistableLoginToken(result) {
  if (!result || typeof result !== 'object') return null;
  const injected = coerceTokenString(result[LOGIN_HEADER_TOKEN_FIELD]);
  if (injected) return injected;
  return walkForToken(result, 0, 12);
}
